import React, { useState, useEffect, useRef } from 'react';
import { ToastMessage } from '@/types';
import Icon from '@/components/common/iconedLinks/Icon';

import styles from './Toast.module.scss';
import { URLS } from '@/constants/urls';

const AUTO_CLOSE_DELAY = 6000;
const LONG_MESSAGE_SIZE = 120;

type ToastProps = {
  t: (k: string) => string,
  toast: ToastMessage;
  onClose: () => void
}

export const Toast = ({ t, toast, onClose }: ToastProps): React.JSX.Element => {
  const { msg, err, apiError, options } = toast;
  const { autoClose = true, duration = AUTO_CLOSE_DELAY } = options ?? {};

  const timedAutoClosing = useRef(null);
  const [autoCloseDelay, setAutoCloseDelay] = useState(duration);
  const [displayToast, setDisplayToast] = useState(false);
  const [message, setMessage] = useState(msg);

  const toastStyles = [
    styles['custom-toast'],
    err && styles.error,
    !displayToast && styles['on-exit']
  ].filter(Boolean).join(' ');

  const onExit = () => {
    setDisplayToast(false);
    setTimeout(()=> void onClose(), 900);
  };

  useEffect(() => {
    const messageLength = msg.length;
    if (messageLength > LONG_MESSAGE_SIZE) {
      setAutoCloseDelay(autoCloseDelay + Math.trunc((messageLength / LONG_MESSAGE_SIZE)) * 1000);
    }
  }, [msg]);

  useEffect(() => {
    let toastMessage = msg ? msg.includes(' ') ? msg : t(msg) : '';

    if (apiError) {
      if (msg === 'errors.genericMessage.main') {
        if (apiError?.code) {
          toastMessage += ` (code: ${apiError.code})`;
        } else if (apiError?.status) {
          toastMessage += ` (status: ${apiError.status})`;
        }
      }
    }

    if (toastMessage) {
      setMessage(toastMessage);
      setDisplayToast(true);
    }

    if (autoClose && !err) {
      timedAutoClosing.current = setTimeout(() => void onExit(), autoCloseDelay);
    }

    return () => {
      if (autoClose) {
        clearTimeout(timedAutoClosing.current);
      }
    };
  }, [apiError, msg, t]);

  const getMessageToDisplay = (error: unknown): string => {
    let message = t('toast.success');
    if (error) {
      message = t('toast.error');
    }
    return message;
  };

  return displayToast && (
    <div className={styles.container} data-test-id="custom-toast" data-rbf="custom-toast">
      <div className={toastStyles} data-test-id={err ? 'toast-error' : null}>
        <Icon
          src={err ? URLS.DELETE_CIRCLE_ICON_BLACK : URLS.CHECKED_CIRCLE_ICON_BLACK}
          alt={err ? 'error' : 'success'}
          data-test-id="toast-image"
          className={styles.icon}
        />
        <h2 className={styles.title}>{getMessageToDisplay(err)}</h2>
        <span className={styles.text} data-test-id="toast-message" data-rbf="toast-message">
          {message}
        </span>
        <Icon
          src={URLS.ORION_CLOSE_BLACK}
          alt="close"
          height={12}
          width={12}
          onClick={onExit}
          className={styles.close}
        />
      </div>
    </div>
  );
};

export default Toast;
